import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EventAvailable, PersonAddAlt1Outlined, PersonOutline } from "@mui/icons-material";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { openCustomerDialog } from "../../Store/Slices/customerSlice";
import {
  reserveTable,
  cancelReservation,
  selectTables,
  selectZones,
} from "../../Store/Slices/restaurantSlice";

const ReservationButton = ({ sx }) => {
  const dispatch = useDispatch();
  const tables = useSelector(selectTables);
  const zones = useSelector(selectZones);

  const [open, setOpen] = useState(false);
  const [tableType, setTableType] = useState("");
  const [tableId, setTableId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState("");
  const [arrangements, setArrangements] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const awaitingRef = useRef(false);

  useEffect(() => {
    const handleCustomerChange = (event) => {
      if (!awaitingRef.current) return;
      const customer = event?.detail?.customer;
      if (!customer) return;
      awaitingRef.current = false;
      setCustomerName(customer.custName || "");
      setPhone(
        customer.custMobile ||
          (customer.custId != null ? String(customer.custId) : ""),
      );
    };
    window.addEventListener("pos:customer-changed", handleCustomerChange);
    return () =>
      window.removeEventListener("pos:customer-changed", handleCustomerChange);
  }, []);

  const handleSelectCustomer = () => {
    awaitingRef.current = true;
    dispatch(openCustomerDialog("reservation"));
  };

  const dineInTables = useMemo(
    () => tables.filter((t) => !t.isOnline),
    [tables],
  );

  const tableTypes = useMemo(
    () => zones.filter((z) => z !== "Online / Delivery"),
    [zones],
  );

  const tablesForType = useMemo(
    () =>
      tableType
        ? dineInTables.filter((t) => t.zone === tableType)
        : dineInTables,
    [dineInTables, tableType],
  );

  const reservedTables = useMemo(
    () => dineInTables.filter((t) => t.reservation),
    [dineInTables],
  );

  const resetForm = () => {
    setTableType("");
    setTableId("");
    setDate("");
    setTime("");
    setGuests("");
    setArrangements("");
    setCustomerName("");
    setPhone("");
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSave = () => {
    if (!tableId || !date || !time) return;
    dispatch(reserveTable({ tableId, date, time, guests, arrangements, customerName, phone }));
    handleClose();
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <>
      <Button
        variant="outlined"
        size="large"
        startIcon={<EventAvailable sx={{ fontSize: "1.5rem !important" }} />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: "1.05rem",
          px: 2.5,
          py: 1.2,
          borderRadius: 2,
          color: "#0f766e",
          borderColor: "#0f766e",
          "&:hover": { borderColor: "#0b5952", backgroundColor: "#f0fdfa" },
          ...sx,
        }}
      >
        Reserve Table
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reserve a Table</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Customer
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {customerName || phone ? (
                  <Chip
                    icon={<PersonOutline />}
                    label={
                      customerName
                        ? `${customerName}${phone ? ` · ${phone}` : ""}`
                        : phone
                    }
                    onDelete={() => {
                      setCustomerName("");
                      setPhone("");
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No customer selected
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PersonAddAlt1Outlined />}
                  onClick={handleSelectCustomer}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {customerName || phone
                    ? "Change Customer"
                    : "Select Customer"}
                </Button>
              </Stack>
            </div>

            <>
                <TextField
                  select
                  label="Table Type"
                  value={tableType}
                  onChange={(e) => {
                    setTableType(e.target.value);
                    setTableId("");
                  }}
                  fullWidth
                >
                  {tableTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Table"
                  value={tableId}
                  onChange={(e) => setTableId(e.target.value)}
                  fullWidth
                  disabled={!tableType}
                >
                  {tablesForType.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} · {t.seats} seats · {t.zone}
                      {t.reservation ? " (reserved)" : ""}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label="Time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>

                <TextField
                  type="number"
                  label="Number of Guests"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  inputProps={{ min: 1 }}
                  fullWidth
                />

                <TextField
                  label="Remarks / Prior Arrangements"
                  value={arrangements}
                  onChange={(e) => setArrangements(e.target.value)}
                  placeholder="e.g. birthday setup, high chair, window seat..."
                  multiline
                  minRows={2}
                  fullWidth
                />
              </>

            {reservedTables.length > 0 && (
              <div>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, mt: 1 }}
                >
                  Existing Reservations
                </Typography>
                <Stack spacing={1}>
                  {reservedTables.map((t) => (
                    <Stack
                      key={t.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{t.name}</strong> — {t.reservation.date}{" "}
                        {t.reservation.time}
                        {t.reservation.guests
                          ? ` · ${t.reservation.guests} guests`
                          : ""}
                        {t.reservation.customerName
                          ? ` · ${t.reservation.customerName}`
                          : ""}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => dispatch(cancelReservation(t.id))}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </div>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!tableId || !date || !time}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: "#0f766e",
              "&:hover": { backgroundColor: "#0b5952" },
            }}
          >
            Reserve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReservationButton;
